'use client';

import { TranslatableButton, TranslatableLoadingButton } from '@epos/ui/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@mui/material';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { PasswordField } from '@/components/password-field';
import { useAppDispatch } from '@/hooks/use-app-dispatch';
import { setUser } from '@/store/reducers/auth-reducer';

import { loginAction, LoginReturn } from '../../login-action';
import { LoginFormProps } from './login-form.props';
import { LoginFormDto, LoginFormFieldNames, loginFormSchema } from './login-form.schema';

const SUBMIT_BUTTON_LABEL = 'Login';
const REGISTER_BUTTON_LABEL = 'Register New Account';
const FORGOT_PASSWORD_LABEL = 'Forgot Password?';

const LoginForm: React.FC<LoginFormProps> = ({ subdomain }) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<LoginFormDto>({
    resolver: yupResolver(loginFormSchema),
    defaultValues: loginFormSchema.getDefault()
  });
  const dispatch = useAppDispatch();
  const router = useRouter();
  const params = useParams();

  const successHandler = async (form: LoginDto) => {
    const res = await loginAction(form, subdomain);

    if (res?.message) {
      toast.error(res.message);
    } else if ((res as LoginReturn)?.isSendOtp) {
      router.push(`login/verify`);
    } else {
      dispatch(setUser(res));
      await new Promise((resolve) => {
        router.prefetch(`/${params.subdomain}/overview`);
        setTimeout(() => {
          resolve(true);
        }, 300);
      });
      router.replace(`/${params.subdomain}/overview`);
    }
  };

  const handleForgotPasswordClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    router.push(`/${params.subdomain}/forgot-password`);
  };

  return (
    <form noValidate className='flex w-full flex-col gap-8' onSubmit={handleSubmit(successHandler)}>
      <Controller
        control={control}
        name={LoginFormFieldNames.phoneNumber}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            label='Phone number'
            onChange={onChange}
            value={value}
            error={Boolean(error)}
            helperText={error?.message}
          />
        )}
      />

      <div className='flex w-full flex-col gap-4'>
        <Controller
          control={control}
          name={LoginFormFieldNames.password}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <PasswordField
              label='Password'
              onChange={onChange}
              value={value}
              error={Boolean(error)}
              helperText={error?.message}
            />
          )}
        />

        <div className='flex justify-end'>
          <TranslatableButton
            variant='text'
            href={`/${params.subdomain}/forgot-password`}
            onClick={handleForgotPasswordClick}
            sx={(theme) => ({
              textTransform: 'none',
              color: theme.palette.primary.main,
              padding: 0,
              minWidth: 0,
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: theme.typography.body1.lineHeight,
              verticalAlign: 'baseline',
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent',
                color: theme.palette.primary.dark
              },
              border: 'none',
              cursor: 'pointer',
              fontFamily: theme.typography.fontFamily
            })}
          >
            {FORGOT_PASSWORD_LABEL}
          </TranslatableButton>
        </div>
      </div>

      <div className='flex w-full flex-col gap-2 md:flex-row'>
        <TranslatableLoadingButton type='submit' className='flex-grow' loading={isSubmitting}>
          {SUBMIT_BUTTON_LABEL}
        </TranslatableLoadingButton>
        <TranslatableButton
          variant='outlined'
          className='!border-1.5 !flex-grow whitespace-nowrap md:-order-1 md:max-w-[50%]'
          component={Link}
          href={`/${params.subdomain}/register`}
          disabled={isSubmitting}
        >
          {REGISTER_BUTTON_LABEL}
        </TranslatableButton>
      </div>
    </form>
  );
};

export default LoginForm;
